import React, { useContext, useEffect, useMemo } from 'react';
import TableHead from './TableHead';
import VirtualTable from './VirtualTable';

import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import CodeRow from './CodesList/CodeListItems/CodeRow';
import { actions } from '../../../../../../context';
import BulkActionsBar from '../BulkActionsBar';
import { DaylightContext } from '../../DaylightCodeMapper';
import { compareCodes } from './sortUtils';

const TABLE_HEADER_HEIGHT = 100;

const CodesTable = () => {
    const dispatch = useDispatch();
    const { codeUsageStatsMap } = useContext(DaylightContext);

    const daylightCodes = useSelector(
        (state) => state.daylight.daylightCodes,
        shallowEqual
    );
    const codeNameFilter = useSelector((state) => state.daylight.codeNameFilter);
    const codeCategoryFilter = useSelector((state) => state.daylight.codeCategoryFilter);
    const codeDescriptionFilter = useSelector((state) => state.daylight.codeDescriptionFilter);
    const codeLocationFilter = useSelector((state) => state.daylight.codeLocationFilter);
    const codeMappingFilter = useSelector((state) => state.daylight.codeMappingFilter);
    const codeHasCommentsFilter = useSelector((state) => state.daylight.codeHasCommentsFilter);
    const codeIsMarkedCompletedFilter = useSelector((state) => state.daylight.codeIsMarkedCompletedFilter);
    const codeNeedsReviewFilter = useSelector((state) => state.daylight.codeNeedsReviewFilter);
    const codeMarkedDisabledFilter = useSelector((state) => state.daylight.codeMarkedDisabledFilter);
    const codeIsBundleFilter = useSelector((state) => state.daylight.codeIsBundleFilter);
    const sortConfig = useSelector((state) => state.daylight.sortConfig, shallowEqual);
    const selectedCodeUsageDetail = useSelector((state) => state.daylight.selectedCodeUsageDetail, shallowEqual);

    const filteredCodes = useMemo(() => {
        return daylightCodes.filter((code) =>
            filterCodeName(code.code, codeNameFilter) &&
            filterCodeCategory(code.codeCategory, codeCategoryFilter) &&
            filterCodeLocation(code.locationId, codeLocationFilter) &&
            filterCodeMappings(code.mappings, codeMappingFilter) &&
            filterCodeDescription(code.codeDescription, codeDescriptionFilter) &&
            filterHasComments(code.mappings, codeHasCommentsFilter) &&
            filterIsMarkedCompleted(code.confirmationStatus, codeIsMarkedCompletedFilter) &&
            filterNeedsReview(code.needsReview, codeNeedsReviewFilter) &&
            filterMarkedDisabled(code.markedDisabled, codeMarkedDisabledFilter) &&
            filterIsBundle(code.isBundleParent, codeIsBundleFilter)
        );
    }, [
        daylightCodes, codeNameFilter, codeCategoryFilter, codeDescriptionFilter,
        codeLocationFilter, codeMappingFilter, codeHasCommentsFilter,
        codeIsMarkedCompletedFilter, codeNeedsReviewFilter, codeMarkedDisabledFilter,
        codeIsBundleFilter
    ]);

    const sortedCodes = useMemo(() => {
        const codes = sortConfig
            ? [...filteredCodes].sort((firstCode, secondCode) =>
                compareCodes(firstCode, secondCode, sortConfig.field, sortConfig.direction, codeUsageStatsMap, selectedCodeUsageDetail)
            )
            : filteredCodes;
        return codes.map((code, index) => ({ ...code, row: index + 1 }));
    }, [filteredCodes, sortConfig, codeUsageStatsMap, selectedCodeUsageDetail]);

    const sortKey = sortConfig ? `${sortConfig.field}-${sortConfig.direction}` : 'default';

    const filteredCodeIds = useMemo(
        () => filteredCodes.map(code => code.id),
        [filteredCodes]
    );

    useEffect(() => {
        dispatch(actions.daylight.setDisplayedCodesCount(sortedCodes.length));
    }, [sortedCodes.length, dispatch]);

    const codeIndexRowHeights = useMemo(() => {
        return buildRowHeightIndex(sortedCodes);
    }, [sortedCodes]);

    const getItemSize = (index) => codeIndexRowHeights[index].rowHeight || 40;

    function Row({ index }) {
        const { codeIndex, mappingIndex } = codeIndexRowHeights[index];
        return (
            <CodeRow
                key={index}
                code={sortedCodes[codeIndex]}
                codeIndex={codeIndex}
                mappingIndex={mappingIndex}
                filteredCodeIds={filteredCodeIds}
            />
        );
    }

    return (
        <div
            className="codes-table"
            style={{
                ['--table-header-height']: `${TABLE_HEADER_HEIGHT}px`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <div className="table-responsive vt-header" style={{ height: 'var(--table-header-height)', minHeight: 'var(--table-header-height)', flexShrink: 0 }}>
                <table style={{ minHeight: '100px' }} className="fixed-table w-100 bg-light text-dark sticky-top border-bottom text-12">
                    <TableHead filteredCodeIds={filteredCodeIds} />
                </table>
            </div>
            <BulkActionsBar />
            <div
                className="table-responsive vt-body"
                style={{ height: 'calc(100% - var(--table-header-height))', minHeight: 0, flex: 1, overflow: 'hidden' }}
            >
                <VirtualTable
                    width="100%"
                    itemCount={codeIndexRowHeights.length}
                    codeCount={sortedCodes.length}
                    sortKey={sortKey}
                    itemSize={getItemSize}
                    row={Row}
                    footer={<tfoot></tfoot>}
                />
            </div>
        </div>
    );
};

// --- Row height indexing for virtualized table ---

const ROW_HEIGHT_WITH_LABEL = 70;
const ROW_HEIGHT_DEFAULT = 40;
const LABEL_INTERVAL = 5;

const shouldDisplayLabel = (index, items) =>
    index === 0 || (index % LABEL_INTERVAL === 0 && index < items.length - 1);

const buildRowHeightIndex = (codes, codeIndex = null, result = []) => {
    codes.forEach((entry, index) => {
        if (entry?.mappings?.length) {
            buildRowHeightIndex(entry.mappings, index, result);
        } else {
            result.push({
                mappingIndex: index,
                codeIndex: codeIndex ?? index,
                rowHeight: shouldDisplayLabel(index, codes) ? ROW_HEIGHT_WITH_LABEL : ROW_HEIGHT_DEFAULT,
            });
        }
    });
    return result;
};

// --- Filter predicates (pure functions, single responsibility each) ---

const filterCodeName = (code, filter) => {
    if (!filter) return true;
    return code?.toLowerCase().includes(filter.toLowerCase());
};

const filterCodeCategory = (codeCategory, filter) => {
    if (!filter) return true;
    return codeCategory === filter;
};

const filterCodeLocation = (locationId, filter) => {
    if (!filter) return true;
    return locationId === filter;
};

const filterCodeDescription = (codeDescription, filter) => {
    if (!filter) return true;
    return codeDescription?.toLowerCase().includes(filter.toLowerCase());
};

const filterCodeMappings = (mappings, filter) => {
    if (!filter) return true;
    const normalizedFilter = filter.toLowerCase();
    return mappings?.some((mapping) =>
        mapping.taxonomyCodeNodeLabel?.toLowerCase().includes(normalizedFilter) ||
        mapping.codeName?.toLowerCase().includes(normalizedFilter)
    );
};

const filterHasComments = (mappings, filter) => {
    if (filter === null) return true;
    const hasComments = (m) => m.comments !== null && m.comments?.length > 0;
    return filter ? mappings?.some(hasComments) : mappings?.every((m) => !hasComments(m));
};

const filterIsMarkedCompleted = (confirmationStatus, filter) => {
    if (filter === null) return true;
    return filter ? confirmationStatus === 1 : confirmationStatus === 0;
};

const filterNeedsReview = (needsReview, filter) => {
    if (filter === null) return true;
    return filter ? needsReview : !needsReview;
};

const filterMarkedDisabled = (markedDisabled, filter) => {
    if (filter === null) return true;
    return filter ? markedDisabled : !markedDisabled;
};

const filterIsBundle = (isBundleParent, filter) => {
    if (filter === null) return true;
    return filter ? !!isBundleParent : !isBundleParent;
};

export default CodesTable;
